import { useQuery } from '@tanstack/react-query'
import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useMemo, useEffect } from 'react'
import sortBy from 'lodash/sortBy'
import isArray from 'lodash/isArray'

import { getAllProjects, getProjectForms } from '../../../data/dataServices'
import { ENV_ACTIONS } from '../../../../reducers/environmentReducer'

const projectCache = new Map()
const MAX_CONCURRENT_REQUESTS = 10

const limitedPromiseAll = async (promises, limit) => {
	const results = []
	const executing = []

	for (const promise of promises) {
		const p = promise().then((res) => {
			executing.splice(executing.indexOf(p), 1)
			return res
		})
		results.push(p)
		executing.push(p)
		if (executing.length >= limit) await Promise.race(executing)
	}

	return Promise.all(results)
}

export default function useProject(props) {
	const { enabled = true } = props
	const dispatch = useDispatch()
	const { environment } = useSelector((state) => state)

	const loadFormsByProject = useCallback(
		async (projects) => {
			const tasks = projects.map((project) => async () => {
				if (projectCache.has(project.key)) return projectCache.get(project.key)

				try {
					const resp = await getProjectForms(project.key, environment.apiToken)
					let projectObject = { key: project.key, name: project.name, forms: [] }

					if (resp?.data && isArray(resp.data) && resp.data.length > 0) {
						projectObject.forms = sortBy(resp.data, (form) => form.name.toLowerCase())
						projectCache.set(project.key, projectObject)
					}

					return projectObject
				} catch {
					return { key: project.key, name: project.name, forms: [] }
				}
			})

			const results = await limitedPromiseAll(tasks, MAX_CONCURRENT_REQUESTS)
			const allForms = results.flatMap((r) => r.forms)

			if (allForms.length > 0) {
				dispatch({
					type: ENV_ACTIONS.GET_FORMS,
					payload: allForms,
				})
			}

			return results
		},
		[dispatch, environment.apiToken]
	)

	const loadAllProjects = useCallback(async () => {
		dispatch({ type: ENV_ACTIONS.IS_LOADING_PROJECTS, payload: true })

		const resp = await getAllProjects(environment.apiToken)
		const projectsWithForms = await loadFormsByProject(resp?.data ?? [])

		dispatch({ type: ENV_ACTIONS.SET_NEW_PROJECTS, payload: projectsWithForms })
		dispatch({ type: ENV_ACTIONS.IS_LOADING_PROJECTS, payload: false })
		dispatch({ type: ENV_ACTIONS.IS_PROJECT_FORMS_LOADED, payload: true })

		return projectsWithForms
	}, [dispatch, environment.apiToken, loadFormsByProject])

	const { data, isLoading, refetch } = useQuery({
		queryKey: ['projectsWithForms', environment.apiToken],
		queryFn: loadAllProjects,
		enabled: enabled && !!environment.apiToken && !environment.isProjectFormsLoaded,
		staleTime: Infinity,
		cacheTime: Infinity,
	})

	useEffect(() => {
		if (!environment.refresh) return
		dispatch({
			type: ENV_ACTIONS.CLEAR_FORMS,
		})
		refetch()
		dispatch({
			type: ENV_ACTIONS.REFRESH,
			payload: false,
		})
	}, [environment.refresh])

	const projectLoading = useMemo(() => {
		return isLoading || environment.isLoadingProjects
	}, [isLoading, environment.isLoadingProjects])

	return {
		projects: data ?? [],
		projectLoading,
	}
}
